import { useEffect, useMemo, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import baseService from '../../../../services/base';
import {
 Center,
 Container,
 Box,
 Stack,
 Heading,
 Flex,
 Grid,
 Spinner,
 HStack,
 Button,
} from '@chakra-ui/react';
import { certificationSchema } from '../../../../core/validations/certification';
import { QuestionnaireHeaderNew } from '../../../../components/Form';
import { CustomRadioGroup, InputText, Select } from '../../../../components/Input';
import useSWR from 'swr';
import { fetchData } from '../../../../services/fetch';
import { SuccessModal } from '../../../../components/Modals';
import { useNavigate } from 'react-router-dom';
import QuestionCardCertification from '../../../../components/Card/Question-card-certification';

export default function CertificationEditFormSimple({
 defaultValues,
 data,
 mutate,
 surveyId,
}) {
 const navigate = useNavigate();
 const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);

 const { data: templates, isLoading: templatesIsLoading } = useSWR(
  `/survey-template/all`,
  fetchData
 );

 const {
  handleSubmit,
  register,
  control,
  formState: { errors, isDirty, isValid, isSubmitting },
 } = useForm({
  defaultValues,
  resolver: yupResolver(certificationSchema),
  mode: 'onChange',
 });

 const {
  fields: questions,
  append: appendQuestions,
  remove: removeQuestions,
 } = useFieldArray({
  control,
  name: 'questions',
 });

 const onSubmit = async (values) => {
  try {
   const existingQuestionIds = data?.questions?.map((q) => q?.id_question_template) || [];
   const currentQuestionIds = questions?.map((q) => q.id_question_template);

   const removedQuestions = existingQuestionIds
    ?.filter((id) => !currentQuestionIds?.includes(id))
    .map((id) => ({ id: id, delete: true }));

   const newQuestions = questions
    .filter((q) => !existingQuestionIds?.includes(q.id_question_template))
    .map((q) => ({ id: q.id_question_template }));

   const payload = {
    description: values.name,
    surveyQuestions: [...newQuestions, ...removedQuestions],
    type: 'certification',
   };
   await baseService.patch(`survey/certificate/${surveyId}`, payload);
   setIsSubmitSuccessful(true);
  } catch (error) {
   console.error('Error updating the survey', error);
  }
 };

 const addQuestion = (question) => {
  if (!questions.some((q) => q.id_question_template === question.id_question_template)) {
   appendQuestions(question);
  }
 };

 const removeQuestion = (questionId) => {
  const questionIndex = questions.findIndex((q) => q.id_question_template === questionId);
  if (questionIndex !== -1) {
   removeQuestions(questionIndex);
  }
 };

 return (
  <form onSubmit={handleSubmit(onSubmit)}>
   <QuestionnaireHeaderNew
    heading='Editar Questionário'
    isSubmitting={isSubmitting}
    isSubmitEnabled={isValid && isDirty}
    isSubmit={true}
   >
    <Grid maxW='96%' templateColumns='3fr 3fr' gap='6'>
     <InputText
      {...register('name')}
      error={errors.name}
      placeholder='Digite o nome do questionário'
      autoComplete='off'
      w='441px'
     />
     <Flex gap='16px'>
      <CustomRadioGroup
       register={register}
       label='Possui pilares?'
       fieldName='hasPillars'
       display='flex'
       gap='16px'
       alignItems='center'
       isDisabled
       defaultValue='Não'
      />
     </Flex>
    </Grid>
   </QuestionnaireHeaderNew>

   <Center my={['8px', '32px']} px={{ xl: '86px' }}>
    <Container maxW={'1280px'} p={0} minH='80vh'>
     <Box bg='white' borderRadius='12px' overflow='hidden'>
      <Box p='6' borderBottom='2px solid #F5F5FB'>
       <Heading as='h3' fontSize='16px' fontWeight={600}>
        Templates
       </Heading>
      </Box>
      <Box p='6'>
       <TemplateSelectEdit
        templates={templates}
        templatesIsLoading={templatesIsLoading}
        addQuestion={addQuestion}
        existingQuestions={questions}
       />

       <Stack spacing='4' mt='32px'>
        {questions?.map((question, index) => (
         <QuestionCardCertification
          key={index}
          index={index}
          remove={() => removeQuestion(question?.id_question_template)}
          question={question}
         />
        ))}
       </Stack>
      </Box>
     </Box>
     <SuccessModal
      isOpen={isSubmitSuccessful}
      onClose={() => {
       setIsSubmitSuccessful(false);
       mutate();
       navigate('/researches');
      }}
      title='Questionário editado com sucesso!'
     />
    </Container>
   </Center>
  </form>
 );
}

function TemplateSelectEdit({
 templates,
 templatesIsLoading,
 addQuestion,
 existingQuestions,
}) {
 const [questionOptionsKey, setQuestionOptionsKey] = useState(0);
 const [selectedTemplate, setSelectedTemplate] = useState(null);
 const [isSecondSelectEnabled, setIsSecondSelectEnabled] = useState(false);
 const [tempSelectedQuestion, setTempSelectedQuestion] = useState(null);

 const { data: surveyQuestions } = useSWR(
  selectedTemplate ? `/question-template/template/${selectedTemplate}` : null,
  fetchData
 );

 // Memorizar as opções de templates
 const templateOptions = useMemo(
  () =>
   templates?.map((template) => ({
    label: template?.name,
    value: template?.id_survey_template,
   })) || [],
  [templates]
 );

 // Memorizar IDs das questões existentes
 const existingQuestionIds = useMemo(() => {
  return new Set(existingQuestions?.map((q) => q.id_question_template));
 }, [existingQuestions]);

 // Filtrar e mapear as opções de perguntas disponíveis no template selecionado
 const questionOptions = useMemo(() => {
  if (!surveyQuestions?.length) {
   return [];
  }

  const options = [];
  if (existingQuestionIds.size !== surveyQuestions.length) {
   options.push({ label: 'Todas as perguntas', value: 'all' });
  }

  const individualQuestions = surveyQuestions
   ?.filter((q) => !existingQuestionIds.has(q.id_question_template))
   .map((q) => ({
    label: q.questionTitleTemplate?.[0]?.description,
    value: q.id_question_template,
   }));

  return [...options, ...individualQuestions];
 }, [surveyQuestions, existingQuestionIds]);

 const handleTemplateSelect = (value) => {
  setSelectedTemplate(value);
  setIsSecondSelectEnabled(true);
 };

 const handleQuestionSelect = (value) => {
  setTempSelectedQuestion(value);
 };

 const addSelectedQuestion = () => {
  if (tempSelectedQuestion === 'all') {
   const allUnselectedQuestions = surveyQuestions.filter(
    (q) => !existingQuestionIds.has(q.id_question_template)
   );
   allUnselectedQuestions.forEach((q) => addQuestion(q));
  } else {
   const selectedQuestion = surveyQuestions?.find(
    (q) => q?.id_question_template === +tempSelectedQuestion
   );
   if (
    selectedQuestion &&
    !existingQuestionIds.has(selectedQuestion.id_question_template)
   ) {
    addQuestion(selectedQuestion);
   }
  }
  setQuestionOptionsKey((prevKey) => prevKey + 1);
  setTempSelectedQuestion(null);
 };

 if (templatesIsLoading) {
  return (
   <Center py='20px'>
    <Spinner />
   </Center>
  );
 }

 return (
  <HStack spacing={6} align='end' mb='12px'>
   <Select
    label='Template'
    placeholder='Selecione um template'
    options={templateOptions}
    onChange={(e) => handleTemplateSelect(e.target.value)}
   />
   <Select
    key={questionOptionsKey}
    label='Pergunta'
    placeholder='Selecione uma pergunta'
    options={questionOptions}
    isDisabled={!isSecondSelectEnabled || !surveyQuestions}
    onChange={(e) => handleQuestionSelect(e.target.value)}
   />
   <Button
    variant='primary'
    size='medium'
    w='137px'
    minW='137px'
    isDisabled={!tempSelectedQuestion || tempSelectedQuestion.length === 0}
    onClick={addSelectedQuestion}
   >
    Adicionar
   </Button>
  </HStack>
 );
}
